import { createContext, type PropsWithChildren, useContext } from 'react';

import {
  type OrganizationTenantsState,
  useOrganizationTenants,
} from './hooks/useOrganizationTenants';

const OrganizationTenantsContext = createContext<OrganizationTenantsState>({
  tenants: [],
  isLoading: false,
  isError: false,
});

/**
 * Fetches the organization tenants once and shares them with the user list, so every row's
 * organization cell reads the same response instead of each cell subscribing on its own.
 */
export const OrganizationTenantsProvider = ({
  children,
}: PropsWithChildren<unknown>): JSX.Element => {
  const value = useOrganizationTenants();

  return (
    <OrganizationTenantsContext.Provider value={value}>
      {children}
    </OrganizationTenantsContext.Provider>
  );
};

/**
 * Reads the organization tenants provided by `OrganizationTenantsProvider`.
 *
 * Falls back to an empty list when no provider is mounted, so an organization cell rendered
 * outside the advisor list shows the empty value rather than crashing.
 * @returns The tenants with the request's loading and error state
 */
export const useOrganizationTenantsContext = (): OrganizationTenantsState =>
  useContext(OrganizationTenantsContext);
